import React, { useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CloseIcon } from '../icons/Icons';
import { useNotificationStore } from '../../stores/useNotificationStore';
import { NotificationType } from '../../types/notification';

interface AllNotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getIconForType = (type: NotificationType) => {
    switch (type) {
        case 'credits_low':
            return (
                <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            );
        case 'credits_expiring':
            return (
                <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'credits_added':
            return (
                <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'task_completed':
            return (
                <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'task_failed':
            return (
                <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'video_ready':
            return (
                <svg className="w-5 h-5 text-snapfy-accent-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
            );
        case 'welcome':
            return (
                <svg className="w-5 h-5 text-snapfy-accent-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
            );
        case 'promo':
            return (
                <svg className="w-5 h-5 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
            );
        case 'system':
        default:
            return (
                <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
    }
};

const AllNotificationsModal: React.FC<AllNotificationsModalProps> = ({ isOpen, onClose }) => {
  const { notifications, isLoading, fetchNotifications, markAsRead, markAllAsRead } = useNotificationStore();

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleNotificationClick = async (notificationId: string, readAt: string | null) => {
    if (!readAt) {
      await markAsRead(notificationId);
    }
  };

  if (!isOpen) return null;

  const unreadCount = notifications.filter(n => !n.readAt).length;

  return (
    <div 
        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
    >
      <div 
        className="bg-snapfy-surface rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col border border-snapfy-border"
        onClick={e => e.stopPropagation()}
      >
        <header className="p-4 flex items-center justify-between border-b border-snapfy-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-snapfy-text-primary">Todas as Notificações</h2>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-[#7063f7] text-white text-xs font-bold rounded-full">
                {unreadCount} nao lidas
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button 
                onClick={handleMarkAllAsRead}
                className="text-sm text-snapfy-accent-soft hover:text-snapfy-accent-soft transition-colors"
              >
                Marcar todas como lidas
              </button>
            )}
            <button 
              onClick={onClose} 
              className="text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors ml-2"
              aria-label="Fechar modal"
            >
              <CloseIcon />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#7063f7] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-snapfy-text-secondary mt-2">Carregando...</p>
            </div>
          ) : notifications.length === 0 ? (
            <p className="p-8 text-center text-sm text-snapfy-text-secondary">Nenhuma notificacao ainda.</p>
          ) : (
            notifications.map(notification => (
              <div 
                key={notification.id} 
                className={`flex items-start gap-4 p-4 border-b border-snapfy-border cursor-pointer hover:bg-snapfy-surface-hover/50 transition-colors ${!notification.readAt ? 'bg-snapfy-inner/30' : ''}`}
                onClick={() => handleNotificationClick(notification.id, notification.readAt)}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-snapfy-inner flex items-center justify-center mt-1">
                  {getIconForType(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${notification.readAt ? 'text-snapfy-text-secondary' : 'text-snapfy-text-primary'}`}>
                    {notification.title}
                  </p>
                  <p className={`text-sm ${notification.readAt ? 'text-snapfy-text-secondary' : 'text-snapfy-text-secondary'}`}>
                    {notification.message}
                  </p>
                  <p className="text-xs text-snapfy-text-secondary mt-1">
                    {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true, locale: ptBR })}
                  </p>
                </div>
                {!notification.readAt && (
                  <div className="w-2.5 h-2.5 rounded-full bg-[#7063f7] mt-1.5 flex-shrink-0" title="Nao lida"></div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AllNotificationsModal;
